import axios from "axios";

// Talon runs exactly one backend. Requiring an environment variable to name it
// -- correctly, in every environment -- adds a way to break the app and buys
// nothing back. Cloudflare Pages keeps Preview and Production variables as
// separate sets, so a preview build with only Production configured got no
// address at all: the base URL became the string "undefined/api", the SPA
// fallback (`/*  /index.html  200`) answered every call with the HTML index at
// status 200, and the app failed in complete silence -- a login button that
// blinked and did nothing.
//
// So the production API is the default, and the variable overrides it when set.
// Any build works out of the box; a future staging backend just sets the var.
//
// This hard-codes a public hostname that already appears in every user's
// browser on every request. It is not a secret and never was.
//
// CHANGING THIS IS A CUTOVER STEP, NOT A RENAME. The address must already be
// answering on Render -- as a custom domain with its certificate issued --
// before this lands in a build, because a build that falls back to a hostname
// nobody is serving fails exactly the silent way described above. The old
// address stays in the CSP in public/_headers until the move is finished, so
// a build pinned to either one keeps working during the changeover.
const DEFAULT_BACKEND_URL = "https://api.talonnest.app";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONFIGURED =
    RAW_BACKEND_URL && RAW_BACKEND_URL !== "undefined" && RAW_BACKEND_URL !== "null"
        ? RAW_BACKEND_URL
        : DEFAULT_BACKEND_URL;

// Kept so the app can still refuse to start if it is ever left with no address
// at all -- which now requires someone to deliberately blank the default.
export const BACKEND_URL_MISSING = !CONFIGURED;

const BACKEND_URL = CONFIGURED.replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

// A warm request to this API answers in well under a second. The reason these
// numbers are large is the cold start: the backend sleeps after a quiet spell
// and the first request afterwards waits for a whole container to boot, which
// Render puts at "50 seconds or more". With no timeout set at all, axios
// inherits the browser's -- and iOS Safari on cellular gives up on a request
// hanging that long, producing an error with no response attached. The user
// sees a hard failure at 6 AM and concludes the app is broken, when it was
// only asleep.
//
// So: fail fast normally, and allow one slow retry that can outlast a boot.
const REQUEST_TIMEOUT_MS = 15000;
const COLD_START_TIMEOUT_MS = 75000;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    timeout: REQUEST_TIMEOUT_MS,
});

/** A failure where no response ever came back: timeout, DNS, refused, CORS
 *  preflight rejected, aeroplane mode. Distinct from the server answering
 *  with an error, which carries a status and usually a message worth showing. */
export function isTransportFailure(err) {
    return Boolean(err) && !err.response && err.code !== "ERR_CANCELED";
}

// Retry once, and only for methods that are safe to repeat.
//
// GET is idempotent, so a second attempt cannot do damage -- and it is what
// wakes a sleeping backend, since the first screen the app loads is a read.
// Mutations are deliberately NOT retried: a POST that timed out may already
// have been applied on the server, and this is a payroll system. Silently
// re-sending "submit my week" or "approve this timecard" could double-apply
// it. Better to tell the person to try again than to guess on their behalf.
const IDEMPOTENT = new Set(["get", "head", "options"]);

api.interceptors.response.use(
    (r) => {
        // Same failure wearing a different hat: a request that lands on the SPA
        // fallback (wrong base URL, a route the backend does not serve) comes
        // back as 200 text/html. Treat that as the error it actually is, so the
        // caller shows a real message instead of trying to read HTML as data.
        const type = r.headers?.["content-type"] || "";
        if (typeof r.data === "string" && type.includes("text/html")) {
            return Promise.reject(new Error(
                "The app reached a web page instead of the API. Its backend address is probably misconfigured."
            ));
        }
        return r;
    },
    (err) => {
        if (err?.response?.status === 401 && !window.location.pathname.startsWith("/login")) {
            // Let the caller decide; ProtectedRoute will redirect.
        }

        const cfg = err?.config;
        const method = (cfg?.method || "get").toLowerCase();
        if (cfg && !cfg.__retried && isTransportFailure(err) && IDEMPOTENT.has(method)) {
            cfg.__retried = true;
            cfg.timeout = COLD_START_TIMEOUT_MS;
            return api.request(cfg);
        }

        return Promise.reject(err);
    }
);

/** Turn any failed request into one sentence worth showing a person.
 *
 *  This exists because the screens each grew their own version, and every one
 *  of them read `err.response.data.detail` and fell through to a generic
 *  "Something went wrong" when there was no response at all -- which is
 *  precisely the case that needs explaining, and the only one where the
 *  generic message is genuinely all the caller knows. A sleeping backend, a
 *  dead cell signal and a real server crash all landed on the same four words.
 */
export function describeRequestError(err, fallback) {
    if (isTransportFailure(err)) {
        return "Couldn’t reach the server. It may be waking up, which can take up to a minute after a quiet spell — wait a moment and try again.";
    }

    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        const msgs = detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
            .filter(Boolean);
        if (msgs.length) return msgs.join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;

    // The HTML-fallback rejection above is a plain Error with a real message.
    if (!err?.response && typeof err?.message === "string" && err.message.includes("API")) {
        return err.message;
    }

    const status = err?.response?.status;
    if (status >= 500) {
        return "The server hit an error handling that. Please try again in a moment.";
    }
    return fallback || "Something went wrong. Please try again.";
}
